import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * Lưới bento 12 cột (§9.6).
 *
 * Lưới 12 cột tồn tại để **kích thước nói lên mức quan trọng**: thứ chỉ cần
 * nhìn một nhịp (số đếm, trạng thái) chiếm 3–4 cột; thứ phải đọc kỹ (bảng,
 * biểu mẫu) chiếm 8–12 cột. Các khối bằng nhau khẳng định chúng quan trọng
 * bằng nhau, điều thường là không đúng.
 */
export function Bento({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('grid grid-cols-12 gap-4', className)} {...props}>
      {children}
    </div>
  );
}

export interface TileProps extends HTMLAttributes<HTMLElement> {
  /** Lớp span theo breakpoint, ví dụ `sm:col-span-6 lg:col-span-3`. */
  span?: string;
}

/**
 * Một ô trong lưới bento.
 *
 * `col-span-12` **luôn được áp trước**, nên ở màn hẹp mọi ô tự xếp dọc; prop
 * `span` chỉ cung cấp các lớp ghi đè có tiền tố breakpoint (§11.3).
 */
export function Tile({ span = 'col-span-12', className, children, ...props }: TileProps) {
  return (
    <section
      className={cn(
        'rounded-lg border border-default bg-surface shadow-sm',
        'col-span-12',
        span,
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
}

/** Tông biểu tượng của ô, đặt theo **loại việc mà ô đó làm** (§9.6). */
export const ICON_TONE: Record<string, string> = {
  primary: 'text-primary-600',
  success: 'text-success-600',
  warning: 'text-warning-600',
  danger: 'text-danger-600',
  violet: 'text-violet-600',
  teal: 'text-teal-600',
  sky: 'text-sky-600',
  orange: 'text-orange-600',
  cyan: 'text-cyan',
  neutral: 'text-content-muted',
};

export interface TileHeaderProps {
  icon?: ReactNode;
  /** Mặc định `cyan`, trùng tông mục TRỢ LÍ AI. */
  tone?: string;
  title: string;
  /** Một dòng nói ô này dùng để làm gì; bỏ trống khi tiêu đề đã đủ rõ. */
  mo?: string;
  action?: ReactNode;
  className?: string;
}

export function TileHeader({
  icon,
  tone = 'cyan',
  title,
  mo,
  action,
  className,
}: TileHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 border-b border-default px-5 pb-3 pt-4',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        {icon && <span className={cn('mt-0.5 shrink-0', ICON_TONE[tone])}>{icon}</span>}
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-content-primary">{title}</h2>
          {mo && <p className="mt-0.5 text-xs leading-relaxed text-content-secondary">{mo}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function TileBody({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-5 py-4', className)} {...props}>
      {children}
    </div>
  );
}

/** Cặp nền + chữ cho chip biểu tượng của `StatTile`. */
const STAT_TONE: Record<string, string> = {
  primary: 'bg-primary-50 text-primary-700',
  success: 'bg-success-50 text-success-700',
  warning: 'bg-warning-50 text-warning-700',
  danger: 'bg-danger-50 text-danger-700',
  violet: 'bg-violet-50 text-violet-700',
  teal: 'bg-teal-50 text-teal-700',
  sky: 'bg-sky-50 text-sky-700',
  neutral: 'bg-subtle text-content-secondary',
};

export interface StatTileProps {
  /** Nhãn của chỉ số. */
  nhan: string;
  /** Con số. */
  so: ReactNode;
  /** Con số ấy nghĩa là gì. */
  sub?: string;
  icon?: ReactNode;
  tone?: string;
  span?: string;
  className?: string;
}

/**
 * Ô chỉ số (KPI) (§9.6).
 *
 * **Luôn truyền `sub`.** Một con số trần buộc người đọc tự dựng lại đơn vị và
 * mốc so sánh, và họ sẽ dựng sai.
 *
 * Span mặc định cho **4 ô mỗi hàng ở màn lớn, 2 ô ở màn nhỏ, 1 ô ở điện thoại**.
 */
export function StatTile({
  nhan,
  so,
  sub,
  icon,
  tone = 'neutral',
  span = 'sm:col-span-6 lg:col-span-3',
  className,
}: StatTileProps) {
  return (
    <Tile span={span} className={className}>
      <div className="flex items-start gap-3 px-5 py-4">
        {icon && (
          <span
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded',
              STAT_TONE[tone],
            )}
          >
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-xs text-content-secondary">{nhan}</p>
          {/* tabular-nums là bắt buộc cho mọi con số, nếu không số sẽ nhảy (§5.6). */}
          <p className="mt-0.5 text-xl font-semibold leading-tight tabular-nums text-content-primary">
            {so}
          </p>
          {sub && <p className="mt-0.5 truncate text-xs text-content-muted">{sub}</p>}
        </div>
      </div>
    </Tile>
  );
}
